import { AuthError, TokenVerificationError, UnknownError } from '../domain/errors/AuthErrors';
import { AuthState, JWTContextType } from '../domain/types/AuthTypes';
import { User } from '../domain/entities/User';
import {
  CLAIM_DB_ALIAS,
  CLAIM_NAME,
  CLAIM_ROLE,
  coerceTokenToString,
  decodeJwt,
  normalizeFullName
} from '../infrastructure/utils/authUtils';
import { generateCorrelationId, clearCorrelationId } from '../infrastructure/utils/correlationIdUtils';
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { absorbEmbedAccessToken } from './absorbEmbedAccessToken';
import { absorbErpExchangeAssertion } from './absorbErpExchangeAssertion';
import { RAPTOR_EMBED_TOKEN_READY } from './embedAuthEvents';
import authServices from '../main/DependencyInjection';
import { useUserPreferences } from '@/common/application/hooks/userPreferencesHook';
import { forceEmbedFromErpLanding, readEmbeddedModeFromStorage } from '@/common/application/embeddedMode';
import { clearRoleAwareViewModes } from '@/userManagement/application/utils/roleAwareDefaults';
import { getFullStoryDebugInfo } from '@/common/infrastructure/config/fullstoryInit';

import { getEnv } from '@/common/infrastructure/config/env';
import type { PendoUser } from '@/common/infrastructure/config/pendoInit';
const { authService, tokenService, authDomainService } = authServices;

export type AuthenticatedUser = User & { roles: string[]; correlationId?: string };

function buildAuthenticatedUserFromToken(authToken: string): AuthenticatedUser | null {
  const userFromDomain = authDomainService.extractUserFromToken(authToken);
  if (!userFromDomain?.userName) {
    return null;
  }

  const claims = decodeJwt<Record<string, unknown>>(authToken) ?? {};
  const rawName = (claims[CLAIM_NAME] as string | undefined) ?? null;
  const fullName = normalizeFullName(rawName);
  const dbAlias = (claims[CLAIM_DB_ALIAS] as string | undefined) ?? null;

  const roleClaim = claims[CLAIM_ROLE] as string | string[] | undefined;
  const roles: string[] = Array.isArray(roleClaim)
    ? roleClaim
    : typeof roleClaim === 'string'
      ? roleClaim
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean)
      : [];

  return {
    id: userFromDomain?.id ?? userFromDomain?.userId ?? '',
    userName: userFromDomain?.userName ?? 'anonymous',
    dbName: userFromDomain?.dbName,
    fullName: fullName ?? userFromDomain?.fullName ?? undefined,
    dbAlias: dbAlias ?? userFromDomain?.dbAlias ?? undefined,
    termsAccepted: userFromDomain?.termsAccepted ?? false,
    timezoneName: userFromDomain?.timezoneName ?? '',
    userEmail: userFromDomain?.userEmail ?? '',
    userId: userFromDomain?.userId ?? '',
    organizationId: userFromDomain?.organizationId ?? undefined,
    crmLicense: userFromDomain?.crmLicense ?? false,
    crmAdmin: userFromDomain?.crmAdmin ?? false,
    roles,
    correlationId: generateCorrelationId()
  };
}

const initialState: AuthState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  ldIdentityStatus: 'pending'
};

const AuthContext = createContext<JWTContextType | null>(null);
const analyticsDisabled = import.meta.env.VITE_DISABLE_ANALYTICS === 'true' || import.meta.env.VITE_LH === '1';

const buildPendoUser = (user: AuthenticatedUser): PendoUser => {
  // Visitor ID == userName by team convention (Allan: "stable for a given user, not the internal userId").
  // Falls back through fullName → 'anonymous' to match the prior `effectiveUserName` resolution.
  const id = user.userName ?? user.fullName ?? 'anonymous';
  return {
    id,
    email: user.userEmail || undefined,
    full_name: user.fullName,
    role: user.roles?.length ? user.roles.join(',') : undefined,
    accountId: user.dbName ?? 'unknown-account',
    accountName: user.dbName,
    environment: getEnv()
  };
};

const schedulePendoInitialization = (user: AuthenticatedUser): (() => void) | undefined => {
  const pendoUser = buildPendoUser(user);
  if (analyticsDisabled || !pendoUser.id) {
    return undefined;
  }

  const runInitialization = () => {
    void import('@/common/infrastructure/config/pendoInit')
      .then(({ setupPendo }) => setupPendo(pendoUser, import.meta.env.VITE_PENDO_KEY))
      .catch((error) => {
        console.warn('Pendo initialization skipped due to an error', error);
      });
  };

  if (typeof window === 'undefined') {
    const timer = setTimeout(runInitialization, 3500);
    return () => clearTimeout(timer);
  }

  const cleanupFns: Array<() => void> = [];
  const timedRun = () => {
    const timeoutId = window.setTimeout(runInitialization, 3500);
    cleanupFns.push(() => clearTimeout(timeoutId));
  };

  const idleHandle = (
    window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    }
  ).requestIdleCallback?.(() => timedRun(), { timeout: 5000 });

  if (typeof idleHandle === 'number') {
    cleanupFns.push(() => (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback?.(idleHandle));
  } else {
    timedRun();
  }

  return () => {
    cleanupFns.forEach((fn) => fn());
  };
};

const scheduleFullStoryInitialization = (userData: AuthenticatedUser): (() => void) | undefined => {
  if (analyticsDisabled || !userData.userId || !userData.dbName) {
    return undefined;
  }

  const runInitialization = () => {
    void import('@/common/infrastructure/config/fullstoryInit')
      .then(({ setupFullStory }) => setupFullStory(userData, import.meta.env.VITE_FULLSTORY_ORG ?? 'o-247J25-na1'))
      .then(() => {
        if (['development', 'localhost', 'test', 'qa'].includes(getEnv())) {
          console.log(getFullStoryDebugInfo());
        }
      })
      .catch((error) => {
        console.warn('FullStory initialization skipped due to an error', error);
      });
  };

  if (typeof window === 'undefined') {
    const timer = setTimeout(runInitialization, 3500);
    return () => clearTimeout(timer);
  }

  const cleanupFns: Array<() => void> = [];
  const timedRun = () => {
    const timeoutId = window.setTimeout(runInitialization, 3500);
    cleanupFns.push(() => clearTimeout(timeoutId));
  };

  const idleHandle = (
    window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    }
  ).requestIdleCallback?.(() => timedRun(), { timeout: 5000 });

  if (typeof idleHandle === 'number') {
    cleanupFns.push(() => (window as Window & { cancelIdleCallback?: (handle: number) => void }).cancelIdleCallback?.(idleHandle));
  } else {
    timedRun();
  }

  return () => {
    cleanupFns.forEach((fn) => fn());
  };
};

export const AuthProvider = ({ children }: { children: React.ReactElement }) => {
  const { logoutUser } = useUserPreferences();
  const [state, setState] = useState<AuthState>(initialState);
  const [loading, setLoading] = React.useState(true);
  const [loginValues] = React.useState(authService.getSavedCredentials());
  const initRef = React.useRef(false);

  // Keep a stable ref to the LaunchDarkly client so it can be used inside
  // async effects without stale-closure issues.
  const ldClient = useLDClient();
  const ldClientRef = useRef(ldClient);
  ldClientRef.current = ldClient;

  // Monotonically-incrementing generation counter guarding syncLaunchDarklyIdentity's
  // async completion (LLAMS-465). Bumped by logout() so an identify() call started
  // before logout can never have its late success/failure overwrite the state that
  // logout() already reset — see syncLaunchDarklyIdentity/logout below.
  const identityEpochRef = useRef(0);

  /**
   * Sends a multi-context identify() call to LaunchDarkly after login so that
   * feature-flag targeting rules can be applied at both the user and the
   * organisation (client / database) level.
   *
   * HOW TO TARGET PER CLIENT IN LAUNCHDARKLY:
   *   1. Open the flag (e.g. "crm.module.enabled") → Targeting tab.
   *   2. Add a rule: "If organization key is <dbAlias value> → serve true".
   *      The dbAlias comes from the JWT claim and uniquely identifies
   *      each customer database (e.g. "client_abc", "acme_corp").
   *   3. You can also target by user.key for individual user overrides.
   *   4. Keep the default rule as "false" so new clients are off by default.
   *
   * FIELDS AVAILABLE FOR TARGETING:
   *   - organization.key  → dbAlias (primary per-client identifier)
   *   - organization.name → dbName  (human-readable database name)
   *   - user.key          → userId
   *   - user.email        → userEmail
   *   - user.name         → fullName
   */
  const syncLaunchDarklyIdentity = async (user: AuthenticatedUser) => {
    // Claim this attempt's generation. Only the most recent attempt may write
    // ldIdentityStatus — a superseded attempt (identity cleared/changed via
    // logout() before this one settles) has its late resolution discarded below.
    const epoch = ++identityEpochRef.current;
    const commitStatus = (status: AuthState['ldIdentityStatus']) => {
      if (identityEpochRef.current === epoch) {
        setState((prev) => ({ ...prev, ldIdentityStatus: status }));
      }
    };

    if (!ldClientRef.current) {
      commitStatus('failed');
      return;
    }
    try {
      await ldClientRef.current.identify({
        kind: 'multi',
        user: {
          key: user.userId ?? user.userName,
          name: user.fullName ?? user.userName,
          email: user.userEmail ?? ''
        },
        organization: {
          // dbAlias is the per-client database identifier included in the JWT.
          // This is the recommended key to use for per-organisation flag rules.
          key: user.dbAlias ?? user.dbName ?? 'unknown-org',
          name: user.dbName ?? 'unknown'
        }
      });
      commitStatus('succeeded');
    } catch (error) {
      // Non-fatal: flag evaluations will fall back to default values. The CRM
      // gate (LLAMS-465) treats a failed identify as a final denial, never a
      // reason to trust the flag's fallback value.
      console.warn('[LaunchDarkly] Failed to identify user context', error);
      commitStatus('failed');
    }
  };

  useEffect(() => {
    // Prevent multiple initializations
    if (initRef.current) return;
    initRef.current = true;

    let cancelPendoInit: (() => void) | undefined;
    let cancelFullStoryInit: (() => void) | undefined;

    const init = async () => {
      try {
        forceEmbedFromErpLanding();
        absorbErpExchangeAssertion({});

        // TROC-316 / RWT-7864: persist ERP WebView ?token= before cookie-only reads.
        // Do not strip the query yet — if verify fails we must not leave the embed without a token.
        absorbEmbedAccessToken({
          getToken: () => tokenService.getToken(),
          setToken: (token) => tokenService.setToken(token),
          verifyToken: (token) => {
            try {
              return tokenService.verifyToken(token);
            } catch {
              return false;
            }
          }
        });

        const rawToken = tokenService.getToken();
        const authToken = coerceTokenToString(rawToken);

        let tokenOk = false;
        try {
          tokenOk = Boolean(authToken) && authService.verifyToken(authToken);
        } catch {
          tokenOk = false;
        }

        if (!tokenOk) {
          // Keep embed ?token= / sticky ERP embed storage so Try-Me can still open UM after login.
          // Only clear session when there was no embed token/session to begin with.
          const hasEmbedQueryToken =
            typeof window !== 'undefined' && Boolean(new URLSearchParams(window.location.search).get('token')?.trim());
          const hasEmbedSession =
            hasEmbedQueryToken ||
            (typeof window !== 'undefined' && Boolean(new URLSearchParams(window.location.search).get('embed')?.trim())) ||
            readEmbeddedModeFromStorage();
          if (!hasEmbedSession && !authToken) {
            logout();
          } else {
            setState((prev) => ({
              ...prev,
              isLoggedIn: false,
              isInitialized: true,
              user: null
            }));
          }
          return;
        }

        // Verified — safe to remove token from the address bar.
        if (typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          if (params.has('token')) {
            params.delete('token');
            const next = params.toString();
            const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
            window.history.replaceState(null, '', url);
          }
        }

        const mergedUser = buildAuthenticatedUserFromToken(authToken);
        if (!mergedUser) {
          setState((prev) => ({
            ...prev,
            isLoggedIn: false,
            isInitialized: true,
            user: null
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          isLoggedIn: true,
          isInitialized: true,
          user: mergedUser
        }));

        // Identify the authenticated user in LaunchDarkly so that per-client
        // and per-user feature-flag targeting rules take effect immediately.
        void syncLaunchDarklyIdentity(mergedUser);

        cancelPendoInit = schedulePendoInitialization(mergedUser);

        // Schedule FullStory initialization
        cancelFullStoryInit = scheduleFullStoryInitialization(mergedUser);
      } catch (error) {
        if (error instanceof TokenVerificationError) {
          logout();
        }
        // Error handled by logout or state reset
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => {
      cancelPendoInit?.();
      cancelFullStoryInit?.();
    };
  }, []);

  // RWT-7864: AuthProvider sits outside the router; provision→users persists JWT after init.
  useEffect(() => {
    const rehydrateFromEmbedToken = () => {
      setState((prev) => {
        if (prev.isLoggedIn && prev.user) {
          return prev;
        }

        const authToken = coerceTokenToString(tokenService.getToken());
        let tokenOk = false;
        try {
          tokenOk = Boolean(authToken) && authService.verifyToken(authToken);
        } catch {
          tokenOk = false;
        }
        if (!tokenOk) {
          return prev;
        }

        const mergedUser = buildAuthenticatedUserFromToken(authToken);
        if (!mergedUser) {
          return prev;
        }

        void syncLaunchDarklyIdentity(mergedUser);
        return {
          ...prev,
          isLoggedIn: true,
          isInitialized: true,
          user: mergedUser
        };
      });
    };

    window.addEventListener(RAPTOR_EMBED_TOKEN_READY, rehydrateFromEmbedToken);
    return () => window.removeEventListener(RAPTOR_EMBED_TOKEN_READY, rehydrateFromEmbedToken);
  }, []);

  const login = async (
    UserName: string,
    DbName: string,
    password: string,
    rememberMe: boolean,
    TimezoneName?: string,
    TimezoneOffset?: number
  ) => {
    try {
      await authService.login({
        userName: UserName,
        dbName: DbName,
        password,
        rememberMe,
        TimezoneName,
        TimezoneOffset
      });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      await authService.register({ email, password, firstName, lastName });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const logout = () => {
    // Invalidate any in-flight syncLaunchDarklyIdentity() call synchronously, before
    // resetting state below — a late success/failure from the identity being cleared
    // must never overwrite the 'pending' status this logout is about to set (LLAMS-465).
    identityEpochRef.current += 1;

    try {
      authService.logout();
      logoutUser();

      // Reset LaunchDarkly back to an anonymous context on logout so that
      // flag evaluations no longer reflect the previous user's organisation.
      void ldClientRef.current?.identify({ kind: 'user', key: 'anonymous', anonymous: true });

      // Clear user timezone manual selection flag on logout
      // This allows the organization timezone to be set on next login
      try {
        localStorage.removeItem('userTimezoneManuallySelected');
        localStorage.removeItem('orgDefaultTimezone');
      } catch (storageError) {
        console.warn('Failed to clear timezone preferences on logout', storageError);
      }

      // Clear correlationId from localStorage and Sentry on logout
      clearCorrelationId();

      // TROC-233: reset role-aware default view mode so a new login starts on the role default.
      clearRoleAwareViewModes();

      // Do NOT clear ERP embed chrome-hide here (TROC-317). Logout during Try-Me / AuthGuard
      // must keep the WebView chrome-less through interactive login. Only `?embed=0` clears it.

      setState((prev) => ({ ...prev, isLoggedIn: false, isInitialized: true, user: null, ldIdentityStatus: 'pending' }));
    } catch (error) {
      console.warn('Logout failed; state already reset', error);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await authService.resetPassword({ email });
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new UnknownError(error instanceof Error ? error : undefined);
    }
  };

  const updateProfile = () => {};

  const getToken = () => tokenService.getToken();

  const isTokenExpired = () => {
    const token = getToken();
    if (!token) return true;
    return !authService.verifyToken(coerceTokenToString(token));
  };

  const providerValues = useMemo(
    () => ({
      ...state,
      login,
      logout,
      register,
      resetPassword,
      updateProfile,
      loginValues,
      getToken,
      isTokenExpired
    }),
    [state, loginValues]
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={providerValues}>{children}</AuthContext.Provider>;
};

export default AuthContext;
